import { useCanWrite } from '@artic/api-client';
import { Button } from '@artic/ui/button';
import { DisabledReasonTooltip } from '@artic/ui/disabled-reason-tooltip';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArchiveProjectDialog } from './archive-project-dialog';
import type { ProjectPlanningListItem } from './types';
import { UnarchiveProjectButton } from './unarchive-project-button';

interface ProjectListRowActionsProps {
  project: ProjectPlanningListItem;
  onEdit: (project: ProjectPlanningListItem) => void;
}

/**
 * Per-row actions on the Project Index.
 *
 * - Edit (ENG-819) opens the shared EditProjectDialog for the row's project,
 *   on the same gate as the project page's "Update Project" button: draft or
 *   redrafting only (a stored status; released work must be redrafted first).
 *   The dialog itself is hosted once by the page, not per row.
 * - Archive (ENG-981) is offered only while every work order on the project
 *   is a draft or archived — `archive_blocking_work_order_count` counts the
 *   rest, and the server enforces the same rule. An archived row offers
 *   Unarchive instead and can't be edited until then.
 *
 * Disabled actions say why in a tooltip, before anything is clicked.
 */
export function ProjectListRowActions({ project, onEdit }: ProjectListRowActionsProps) {
  const { t } = useTranslation('project');
  const canWrite = useCanWrite('planner');
  const [archiveOpen, setArchiveOpen] = useState(false);

  // Permissions v1: a read-only user has no row actions at all.
  if (!canWrite) return null;

  const isEditableStatus =
    project.project_status === 'draft' || project.project_status === 'redrafting';
  const editDisabledReason = project.is_archived
    ? t('archive.readOnly')
    : isEditableStatus
      ? null
      : t('header.updateProjectDisabled');
  const archiveDisabledReason =
    project.archive_blocking_work_order_count > 0 ? t('archive.blocked') : null;

  return (
    <div className="flex items-center justify-end gap-1">
      <DisabledReasonTooltip reason={editDisabledReason}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(project)}
          disabled={editDisabledReason !== null}
        >
          Edit
        </Button>
      </DisabledReasonTooltip>
      {project.is_archived ? (
        <UnarchiveProjectButton project={project} variant="ghost" size="sm" />
      ) : (
        <DisabledReasonTooltip reason={archiveDisabledReason}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setArchiveOpen(true)}
            disabled={archiveDisabledReason !== null}
          >
            {t('archive.action')}
          </Button>
        </DisabledReasonTooltip>
      )}
      {/* Mounted only while open, like the page's EditProjectDialog. */}
      {archiveOpen && <ArchiveProjectDialog project={project} open onOpenChange={setArchiveOpen} />}
    </div>
  );
}
